import { type ReactNode, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface IUsePopupReturn<T> {
  openPopup: () => Promise<T>;
  renderPopup: (popupRenderer: IPopupRenderer<T>) => ReactNode;
}

export type IPopupRenderer<T> = (props: IPopupProps<T>) => ReactNode;

export interface IPopupProps<T> {
  closePopup: (value?: T | PromiseLike<T>) => void;
  abortPopup: (reason?: any) => void;
}

export const usePopup = <T>(): IUsePopupReturn<T> => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const resolveRef = useRef<(value: T | PromiseLike<T>) => void>();
  const rejectRef = useRef<(reason?: any) => void>();

  const openPopup = useCallback(async () => {
    const promise = new Promise<T>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
    setOpen(true);
    return await promise;
  }, []);
  const closePopup = useCallback((value?: T | PromiseLike<T>) => {
    resolveRef.current && resolveRef.current(value);
    setOpen(false);
  }, []);
  const abortPopup = useCallback((reason?: any) => {
    rejectRef.current && rejectRef.current(reason);
    setOpen(false);
  }, []);

  return {
    openPopup,
    renderPopup: useCallback(
      (
        popupRenderer: IPopupRenderer<T>,
        container: Element = document.body,
      ) => {
        if (!isOpen) {
          return null;
        }

        return createPortal(
          popupRenderer({
            closePopup,
            abortPopup,
          }),
          container,
        );
      },
      [isOpen, closePopup, abortPopup],
    ),
  };
};

export default usePopup;
